/**
 * THE CORE, as a nucleus.
 *
 * His instruction: "the core Panel need to be Dynamic and with some Graphics (make it as a
 * Nucleus with floating elements)". The panel was a still photograph of a station, then a static
 * orbital drawing, and both illustrated the number beside them rather than depicting it.
 *
 * SO IT IS DRIVEN BY THE REAL COUNTS, not by a decorative loop. Each orbiting body is one of the
 * vault's eight node kinds, its size comes from how many of that kind exist, and its colour is
 * that kind's own hue from the semantic vocabulary. A kind with nothing in it does not orbit. The
 * result is that the panel says something true at a glance: the big bodies are what the vault is
 * mostly made of. That distinction is the whole reason this is worth building rather than
 * animating some dots.
 *
 * WHY SVG AND CSS RATHER THAN CANVAS. A requestAnimationFrame loop on a permanent panel costs a
 * frame budget forever, and this thing is on screen every second the board is. Rotating a `<g>`
 * with a CSS keyframe is composited, needs no JS after mount, and stops dead under
 * `prefers-reduced-motion`. It is also crisp at any size, which a 132px canvas is not.
 *
 * ON THE DECK'S "NOTHING ANIMATES WHERE HE READS" RULE. That rule is mine and he has overridden it
 * here deliberately, so this moves. It is kept honest by being SLOW: the fastest ring takes 34
 * seconds to come round. Motion you have to wait for does not compete with a number.
 */

/** The vault's node kinds, in the order they orbit outward. */
const KINDS: readonly { key: string; label: string; token: string; ring: 0 | 1 | 2 }[] = [
  { key: 'person', label: 'People', token: '--hue-know', ring: 0 },
  { key: 'org', label: 'Organisations', token: '--accent', ring: 0 },
  { key: 'topic', label: 'Topics', token: '--hue-intent', ring: 1 },
  { key: 'commitment', label: 'Commitments', token: '--warning', ring: 1 },
  { key: 'meeting', label: 'Meetings', token: '--hue-time', ring: 1 },
  { key: 'thread', label: 'Correspondence', token: '--hue-ask', ring: 2 },
  { key: 'decision', label: 'Decisions', token: '--danger', ring: 2 },
  { key: 'objective', label: 'Objectives', token: '--success', ring: 2 },
];

/** The three orbits: radii in view units, and how long one revolution takes. */
const RINGS = [
  { rx: 26, ry: 10, dur: 34, dir: 1 },
  { rx: 42, ry: 17, dur: 52, dir: -1 },
  { rx: 56, ry: 23, dur: 74, dir: 1 },
] as const;

/**
 * Body radius from a count.
 *
 * Cube root, not linear: threads outnumber objectives by orders of magnitude in his vault, and a
 * linear scale would draw one dot the size of the panel and seven invisible ones. The floor is
 * 1.6 so a kind that exists is always visible, and a kind with zero is dropped entirely rather
 * than drawn at the floor, because a body that means "none" is a lie about the shape of the vault.
 */
function radiusFor(count: number, max: number): number {
  if (count <= 0) return 0;
  const t = Math.cbrt(count) / Math.cbrt(Math.max(max, 1));
  return 1.6 + t * 3.4;
}

export function CoreNucleus({ counts }: { counts: Record<string, number> }): JSX.Element {
  const max = Math.max(1, ...KINDS.map((k) => counts[k.key] ?? 0));
  const present = KINDS.filter((k) => (counts[k.key] ?? 0) > 0);

  return (
    <span className="cdeck-nucleus" aria-hidden="true">
      <svg viewBox="-70 -34 140 68" fill="none">
        {RINGS.map((r, i) => (
          <ellipse
            key={`ring-${i}`}
            cx="0"
            cy="0"
            rx={r.rx}
            ry={r.ry}
            className="cdeck-nucleus-orbit"
          />
        ))}

        {/* One rotating group per ring, so bodies on the same orbit keep their spacing. */}
        {RINGS.map((r, ri) => {
          const onRing = present.filter((k) => k.ring === ri);
          if (onRing.length === 0) return null;
          return (
            <g
              key={`orbit-${ri}`}
              className="cdeck-nucleus-spin"
              style={{
                animationDuration: `${r.dur}s`,
                animationDirection: r.dir === 1 ? 'normal' : 'reverse',
              }}
            >
              {onRing.map((k, i) => {
                // Spread evenly around the ellipse, offset per ring so the three do not line up.
                const a = (i / onRing.length) * Math.PI * 2 + ri * 0.7;
                return (
                  <circle
                    key={k.key}
                    cx={Math.cos(a) * r.rx}
                    cy={Math.sin(a) * r.ry}
                    r={radiusFor(counts[k.key] ?? 0, max)}
                    className="cdeck-nucleus-body"
                    style={{ color: `var(${k.token})` }}
                  >
                    {/* A title, so the colours are legible to someone who did not design them. */}
                    <title>{`${k.label}: ${(counts[k.key] ?? 0).toLocaleString()}`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}

        {/*
          THE INSTRUMENT AROUND THE DATA. "make it very Star-wars themed."

          Everything below is frame, not readout: a targeting reticle with its four tick marks, a
          sweep arm on the outer ring, and an armoured hex at the centre instead of a plain dot.
          None of it encodes anything, which is the point of keeping it strictly to the frame: the
          bodies are the vault and they were not touched to make room for this.
        */}
        <g className="cdeck-nucleus-reticle">
          <circle cx="0" cy="0" r="29" />
          <path d="M-34 0h7M27 0h7M0-25v6M0 19v6" />
        </g>

        {/* A sensor sweep. One arm, one revolution every 9 seconds, so it reads as a machine
            doing something rather than as an animation playing. */}
        <g className="cdeck-nucleus-sweep">
          <path d="M0 0 L56 0" />
        </g>

        <circle cx="0" cy="0" r="13" className="cdeck-nucleus-halo" />
        {/* Hex, not circle: the emblem on his chest, the icon chips and the rail's mark are all
            hexagonal, so the core of the board is too. */}
        <path
          className="cdeck-nucleus-core"
          d="M0-8 6.9-4 6.9 4 0 8-6.9 4-6.9-4z"
        />
        <path className="cdeck-nucleus-ring" d="M0-11 9.5-5.5 9.5 5.5 0 11-9.5 5.5-9.5-5.5z" />
      </svg>
    </span>
  );
}
